import { 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy, 
  limit,
  onSnapshot,
  serverTimestamp,
  addDoc,
  doc,
  getDoc
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { appointmentService, chatService } from './firebaseService';

// Dashboard Service
export const dashboardService = {
  // Calculate dashboard metrics for a user
  async calculateDashboardMetrics(userId, userRole = 'client') {
    try {
      console.log('🔍 Calculating dashboard metrics for user:', userId, 'role:', userRole);
      
      // Get user's bookings/appointments
      const bookingsResult = await appointmentService.getUserAppointments(userId, userRole);
      const bookings = bookingsResult.success ? bookingsResult.data : [];
      
      // Get user's chats
      const chatsResult = await chatService.getUserChats(userId);
      const chats = chatsResult.success ? chatsResult.data : [];
      
      // Calculate metrics
      const totalBookings = bookings.length;
      const pendingBookings = bookings.filter(b => b.status === 'pending' || b.status === 'confirmed').length;
      const completedBookings = bookings.filter(b => b.status === 'completed').length;
      const cancelledBookings = bookings.filter(b => b.status === 'cancelled').length;
      
      const totalChats = chats.length;
      const activeChats = chats.filter(c => c.status === 'active' || c.lastMessageTime).length;
      
      // Calculate unread messages (simplified - count chats with recent activity)
      const unreadMessages = chats.filter(chat => {
        const lastMessageTime = chat.lastMessageTime?.toDate?.() || new Date(chat.lastMessageTime) || new Date(0);
        const now = new Date();
        const hoursSinceLastMessage = (now - lastMessageTime) / (1000 * 60 * 60);
        return hoursSinceLastMessage < 24; // Consider messages from last 24 hours as "recent"
      }).length;
      
      // Calculate total spent (only completed bookings)
      const totalSpent = bookings
        .filter(b => b.status === 'completed' && b.amount)
        .reduce((total, b) => total + (parseFloat(b.amount) || 0), 0);
      
      // Get last activity timestamp
      const allActivities = [
        ...bookings.map(b => ({ type: 'booking', timestamp: b.createdAt?.toDate?.() || new Date(b.createdAt) || new Date(0) })),
        ...chats.map(c => ({ type: 'chat', timestamp: c.updatedAt?.toDate?.() || new Date(c.updatedAt) || new Date(0) }))
      ];
      
      const lastActivity = allActivities.length > 0 ? 
        new Date(Math.max(...allActivities.map(a => a.timestamp))) : null;
      
      // Calculate average consultation time (default 45 minutes)
      const completedBookingsWithDuration = bookings.filter(b => b.status === 'completed');
      const averageConsultationTime = completedBookingsWithDuration.length > 0 ? 
        Math.round(completedBookingsWithDuration.reduce((total, b) => total + (b.duration || 45), 0) / completedBookingsWithDuration.length) : 45;
      
      // Calculate satisfaction rate (default 4.8)
      const bookingsWithRating = bookings.filter(b => b.rating);
      const satisfactionRate = bookingsWithRating.length > 0 ? 
        Math.round((bookingsWithRating.reduce((total, b) => total + (b.rating || 0), 0) / bookingsWithRating.length) * 10) / 10 : 4.8;
      
      // Calculate average response time (default 2.5 hours)
      const responseTime = 2.5; // This would need to be calculated from actual response times
      
      const metrics = {
        totalBookings,
        pendingBookings,
        completedBookings,
        cancelledBookings,
        totalChats,
        activeChats,
        unreadMessages,
        totalSpent: Math.round(totalSpent * 100) / 100, // Round to 2 decimal places
        lastActivity,
        averageConsultationTime,
        satisfactionRate,
        responseTime,
        // Additional metrics for real-time updates
        lastUpdated: new Date(),
        userId,
        userRole
      };
      
      console.log('✅ Dashboard metrics calculated:', metrics);
      return { success: true, data: metrics };
      
    } catch (error) {
      console.error('❌ Error calculating dashboard metrics:', error);
      return { success: false, error: error.message };
    }
  },

  // Calculate lawyer dashboard metrics
  async calculateLawyerDashboardMetrics(lawyerId) {
    try {
      console.log('🔍 Calculating lawyer dashboard metrics for lawyer:', lawyerId);
      
      // Get lawyer's appointments/bookings
      const appointmentsQuery = query(
        collection(db, 'appointments'),
        where('lawyer_id', '==', lawyerId)
      );
      const appointmentsSnapshot = await getDocs(appointmentsQuery);
      const appointments = appointmentsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Get lawyer's chats
      const chatsQuery = query(
        collection(db, 'chats'),
        where('participants', 'array-contains', lawyerId)
      );
      const chatsSnapshot = await getDocs(chatsQuery);
      const chats = chatsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Calculate metrics
      const totalClients = new Set(appointments.map(a => a.client_id)).size;
      const pendingCases = appointments.filter(a => a.status === 'pending' || a.status === 'confirmed').length;
      const messages = chats.length;
      
      // Calculate earnings
      const completedAppointments = appointments.filter(a => a.status === 'completed');
      const totalEarnings = completedAppointments.reduce((total, a) => total + (parseFloat(a.amount) || 0), 0);
      
      // Calculate available balance from wallet (if available)
      let availableBalance = 0;
      try {
        // Try to get wallet data from localStorage first
        const walletData = JSON.parse(localStorage.getItem('lawyer_wallet') || '{}');
        if (walletData.availableBalance) {
          availableBalance = parseFloat(walletData.availableBalance) || 0;
          console.log('💰 Wallet data found in localStorage:', walletData);
        }
        
        // Also try to get from other possible localStorage keys
        const balanceData = JSON.parse(localStorage.getItem('lawyer_balance') || '{}');
        if (balanceData.balance && availableBalance === 0) {
          availableBalance = parseFloat(balanceData.balance) || 0;
          console.log('💰 Balance data found in localStorage:', balanceData);
        }
        
                 // Check if there's a wallet service available
         if (availableBalance === 0) {
           // Try to import and use wallet service
           try {
             const walletService = await import('./walletService');
             console.log('💰 Wallet service imported successfully:', walletService);
             
             const walletResult = await walletService.default.getLawyerWallet(lawyerId);
             console.log('💰 Wallet service call result:', walletResult);
             
             if (walletResult.success && walletResult.data.availableBalance) {
               availableBalance = parseFloat(walletResult.data.availableBalance) || 0;
               console.log('💰 Wallet data fetched from service:', walletResult.data);
             } else {
               console.log('💰 Wallet service returned no data or failed:', walletResult);
             }
           } catch (walletError) {
             console.error('💰 Wallet service import/usage error:', walletError);
             console.log('Wallet service not available, using fallback calculation');
           }
         }
      } catch (error) {
        console.error('Error getting wallet data:', error);
      }
      
      // If still no wallet data, calculate from completed appointments (80% after platform commission)
      if (availableBalance === 0) {
        availableBalance = totalEarnings * 0.8;
        console.log('💰 Using fallback calculation from earnings:', availableBalance);
      }
      
      console.log('💰 Final available balance calculated:', availableBalance);
      
      // Calculate monthly earnings
      const monthStart = new Date();
      monthStart.setDate(1);
      monthStart.setHours(0, 0, 0, 0);
      const monthlyAppointments = completedAppointments.filter(a => {
        const appointmentDate = a.createdAt?.toDate?.() || new Date(a.createdAt);
        return appointmentDate >= monthStart;
      });
      const monthlyEarnings = monthlyAppointments.reduce((total, a) => total + (parseFloat(a.amount) || 0), 0);
      
      // Calculate consultation types
      const consultationTypes = {
        videoCall: { count: 0, earnings: 0 },
        phoneCall: { count: 0, earnings: 0 },
        inPerson: { count: 0, earnings: 0 }
      };
      
      completedAppointments.forEach(appointment => {
        const consultationType = appointment.consultation_type || 'inPerson';
        const amount = parseFloat(appointment.amount) || 0;
        
        if (consultationTypes[consultationType]) {
          consultationTypes[consultationType].count++;
          consultationTypes[consultationType].earnings += amount;
        }
      });
      
      // Calculate average rating
      const appointmentsWithRating = appointments.filter(a => a.rating);
      const averageRating = appointmentsWithRating.length > 0 ? 
        Math.round((appointmentsWithRating.reduce((total, a) => total + (a.rating || 0), 0) / appointmentsWithRating.length) * 10) / 10 : 4.8;
      
      const totalReviews = appointmentsWithRating.length;
      
      // Get last activity
      const allActivities = [
        ...appointments.map(a => ({ type: 'appointment', timestamp: a.createdAt?.toDate?.() || new Date(a.createdAt) || new Date(0) })),
        ...chats.map(c => ({ type: 'chat', timestamp: c.updatedAt?.toDate?.() || new Date(c.updatedAt) || new Date(0) }))
      ];
      
      const lastActivity = allActivities.length > 0 ? 
        new Date(Math.max(...allActivities.map(a => a.timestamp))) : null;
      
      const metrics = {
        totalClients,
        totalEarnings: Math.round(totalEarnings * 100) / 100,
        pendingCases,
        messages,
        availableBalance: Math.round(availableBalance * 100) / 100,
        monthlyEarnings: Math.round(monthlyEarnings * 100) / 100,
        averageRating,
        totalReviews,
        consultationTypes,
        lastActivity,
        lastUpdated: new Date(),
        lawyerId
      };
      
      // Try to get lawyer's name from profile
      let lawyerName = 'Lawyer';
      try {
                 const lawyerProfile = JSON.parse(localStorage.getItem('lawyer_profile') || '{}');
         if (lawyerProfile.full_name) {
           lawyerName = lawyerProfile.full_name;
                 } else {
           // If not in localStorage, try to get from Firestore
           try {
             const firebaseService = await import('./firebaseService');
             console.log('🔍 firebaseService imported:', firebaseService);
             
             if (firebaseService.lawyerService && firebaseService.lawyerService.getLawyerByUserId) {
               const profileResult = await firebaseService.lawyerService.getLawyerByUserId(lawyerId);
               if (profileResult.success && profileResult.data?.full_name) {
                 // Store the profile in localStorage for future use
                 localStorage.setItem('lawyer_profile', JSON.stringify(profileResult.data));
                 lawyerName = profileResult.data.full_name;
                 console.log('👤 Lawyer name fetched from Firestore:', lawyerName);
               }
             } else {
               console.error('❌ firebaseService.lawyerService.getLawyerByUserId is not available');
             }
           } catch (firebaseError) {
             console.error('Error fetching lawyer profile from Firestore:', firebaseError);
           }
         }
      } catch (error) {
        console.error('Error getting lawyer name from localStorage:', error);
      }
      
      // Add lawyer name to metrics
      metrics.lawyerName = lawyerName;
      
      console.log('✅ Lawyer dashboard metrics calculated:', metrics);
      return { success: true, data: metrics };
      
    } catch (error) {
      console.error('❌ Error calculating lawyer dashboard metrics:', error);
      return { success: false, error: error.message };
    }
  },

  // Get recent activities for a user
  async getRecentActivities(userId, limit = 10) {
    try {
      console.log('🔍 Fetching recent activities for user:', userId);
      
      const activities = [];
      
      // Get recent bookings
      const bookingsResult = await appointmentService.getUserAppointments(userId, 'client');
      if (bookingsResult.success) {
        const recentBookings = bookingsResult.data
          .sort((a, b) => {
            const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt) || new Date(0);
            const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt) || new Date(0);
            return dateB - dateA;
          })
          .slice(0, 5);
        
        recentBookings.forEach(booking => {
          activities.push({
            id: `booking_${booking.id}`,
            type: 'booking',
            title: `Booking ${booking.status}`,
            description: `Consultation with ${booking.lawyerName || 'lawyer'} on ${booking.appointment_date}`,
            icon: 'booking',
            timestamp: booking.createdAt?.toDate?.() || new Date(booking.createdAt) || new Date(),
            priority: booking.status === 'pending' ? 'high' : 'medium',
            data: booking
          });
        });
      }
      
      // Get recent chat activities
      const chatsResult = await chatService.getUserChats(userId);
      if (chatsResult.success) {
        const recentChats = chatsResult.data
          .sort((a, b) => {
            const dateA = a.updatedAt?.toDate?.() || new Date(a.updatedAt) || new Date(0);
            const dateB = b.updatedAt?.toDate?.() || new Date(b.updatedAt) || new Date(0);
            return dateB - dateA;
          })
          .slice(0, 5);
        
        recentChats.forEach(chat => {
          if (chat.lastMessage) {
            activities.push({
              id: `chat_${chat.id}`,
              type: 'chat',
              title: 'New message received',
              description: chat.lastMessage.length > 50 ? 
                `${chat.lastMessage.substring(0, 50)}...` : chat.lastMessage,
              icon: 'message',
              timestamp: chat.lastMessageTime?.toDate?.() || new Date(chat.lastMessageTime) || new Date(),
              priority: 'medium',
              data: chat
            });
          }
        });
      }
      
      // Sort all activities by timestamp (most recent first)
      activities.sort((a, b) => b.timestamp - a.timestamp);
      
      // Return limited number of activities
      const limitedActivities = activities.slice(0, limit);
      
      console.log('✅ Recent activities fetched:', limitedActivities.length);
      return { success: true, data: limitedActivities };
      
    } catch (error) {
      console.error('❌ Error fetching recent activities:', error);
      return { success: false, error: error.message };
    }
  },

  // Add a new activity
  async addActivity(userId, activity) {
    try {
      const activityWithMetadata = {
        ...activity,
        userId,
        timestamp: new Date(),
        createdAt: serverTimestamp()
      };
      
      // Store in localStorage for immediate access
      const savedActivities = JSON.parse(localStorage.getItem('recentActivity') || '[]');
      const updatedActivities = [activityWithMetadata, ...savedActivities.slice(0, 49)]; // Keep last 50
      localStorage.setItem('recentActivity', JSON.stringify(updatedActivities));
      
      // Also store in Firestore for persistence
      try {
        const activityRef = await addDoc(collection(db, 'userActivities'), activityWithMetadata);
        console.log('✅ Activity saved to Firestore:', activityRef.id);
      } catch (firestoreError) {
        console.warn('⚠️ Could not save activity to Firestore:', firestoreError);
        // Continue with localStorage only if Firestore fails
      }
      
      return { success: true, data: activityWithMetadata };
      
    } catch (error) {
      console.error('❌ Error adding activity:', error);
      return { success: false, error: error.message };
    }
  },

  // Get real-time dashboard updates
  subscribeToDashboardUpdates(userId, callback) {
    try {
      console.log('🔍 Setting up real-time dashboard subscription for user:', userId);
      
      let unsubscribeFunctions = [];
      
      // Subscribe to appointments
      const appointmentsQuery = query(
        collection(db, 'appointments'),
        where('client_id', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(10)
      );
      
      const appointmentsUnsubscribe = onSnapshot(appointmentsQuery, (snapshot) => {
        const appointments = [];
        snapshot.forEach(doc => {
          appointments.push({ id: doc.id, ...doc.data() });
        });
        
        // Trigger metrics recalculation
        this.calculateDashboardMetrics(userId, 'client').then(metricsResult => {
          if (metricsResult.success) {
            callback({
              type: 'dashboard_update',
              metrics: metricsResult.data,
              appointments,
              source: 'appointments'
            });
          }
        });
      });
      
      unsubscribeFunctions.push(appointmentsUnsubscribe);
      
      // Subscribe to chats
      const chatsQuery = query(
        collection(db, 'chats'),
        where('participants', 'array-contains', userId),
        orderBy('updatedAt', 'desc'),
        limit(10)
      );
      
      const chatsUnsubscribe = onSnapshot(chatsQuery, (chatSnapshot) => {
        const chats = [];
        chatSnapshot.forEach(doc => {
          chats.push({ id: doc.id, ...doc.data() });
        });
        
        // Trigger metrics recalculation
        this.calculateDashboardMetrics(userId, 'client').then(metricsResult => {
          if (metricsResult.success) {
            callback({
              type: 'dashboard_update',
              metrics: metricsResult.data,
              chats,
              source: 'chats'
            });
          }
        });
      });
      
      unsubscribeFunctions.push(chatsUnsubscribe);
      
      // Subscribe to user activities
      const activitiesQuery = query(
        collection(db, 'userActivities'),
        where('userId', '==', userId),
        orderBy('timestamp', 'desc'),
        limit(10)
      );
      
      const activitiesUnsubscribe = onSnapshot(activitiesQuery, (activitySnapshot) => {
        const activities = [];
        activitySnapshot.forEach(doc => {
          activities.push({ id: doc.id, ...doc.data() });
        });
        
        callback({
          type: 'activities_update',
          activities,
          source: 'activities'
        });
      });
      
      unsubscribeFunctions.push(activitiesUnsubscribe);
      
      // Return unsubscribe function
      return () => {
        unsubscribeFunctions.forEach(unsubscribe => {
          if (unsubscribe && typeof unsubscribe === 'function') {
            unsubscribe();
          }
        });
      };
      
    } catch (error) {
      console.error('❌ Error setting up dashboard subscription:', error);
      return null;
    }
  },

  // Subscribe to lawyer dashboard updates
  subscribeToLawyerDashboardUpdates(lawyerId, callback) {
    try {
      console.log('🔍 Setting up real-time lawyer dashboard subscription for lawyer:', lawyerId);
      
      let unsubscribeFunctions = [];
      
      // Subscribe to appointments
      const appointmentsQuery = query(
        collection(db, 'appointments'),
        where('lawyer_id', '==', lawyerId)
      );
      
      const appointmentsUnsubscribe = onSnapshot(appointmentsQuery, (snapshot) => {
        const appointments = [];
        snapshot.forEach(doc => {
          appointments.push({ id: doc.id, ...doc.data() });
        });
        
        // Trigger metrics recalculation
        this.calculateLawyerDashboardMetrics(lawyerId).then(metricsResult => {
          if (metricsResult.success) {
            callback({
              type: 'lawyer_dashboard_update',
              metrics: metricsResult.data,
              appointments,
              source: 'appointments'
            });
          }
        });
      });
      
      unsubscribeFunctions.push(appointmentsUnsubscribe);
      
      // Subscribe to chats
      const chatsQuery = query(
        collection(db, 'chats'),
        where('participants', 'array-contains', lawyerId)
      );
      
      const chatsUnsubscribe = onSnapshot(chatsQuery, (chatSnapshot) => {
        const chats = [];
        chatSnapshot.forEach(doc => {
          chats.push({ id: doc.id, ...doc.data() });
        });
        
        // Trigger metrics recalculation
        this.calculateLawyerDashboardMetrics(lawyerId).then(metricsResult => {
          if (metricsResult.success) {
            callback({
              type: 'lawyer_dashboard_update',
              metrics: metricsResult.data,
              chats,
              source: 'chats'
            });
          }
        });
      });
      
      unsubscribeFunctions.push(chatsUnsubscribe);
      
      // Return unsubscribe function
      return () => {
        unsubscribeFunctions.forEach(unsubscribe => {
          if (unsubscribe && typeof unsubscribe === 'function') {
            unsubscribe();
          }
        });
      };
      
    } catch (error) {
      console.error('❌ Error setting up lawyer dashboard subscription:', error);
      return null;
    }
  },

  // Get real-time metrics updates only
  subscribeToMetricsUpdates(userId, callback) {
    try {
      console.log('🔍 Setting up real-time metrics subscription for user:', userId);
      
      // Subscribe to appointments and chats for metrics updates
      const appointmentsQuery = query(
        collection(db, 'appointments'),
        where('client_id', '==', userId)
      );
      
      const chatsQuery = query(
        collection(db, 'chats'),
        where('participants', 'array-contains', userId)
      );
      
      let unsubscribeFunctions = [];
      
      const appointmentsUnsubscribe = onSnapshot(appointmentsQuery, () => {
        this.calculateDashboardMetrics(userId, 'client').then(metricsResult => {
          if (metricsResult.success) {
            callback(metricsResult.data);
          }
        });
      });
      
      const chatsUnsubscribe = onSnapshot(chatsQuery, () => {
        this.calculateDashboardMetrics(userId, 'client').then(metricsResult => {
          if (metricsResult.success) {
            callback(metricsResult.data);
          }
        });
      });
      
      unsubscribeFunctions.push(appointmentsUnsubscribe, chatsUnsubscribe);
      
      return () => {
        unsubscribeFunctions.forEach(unsubscribe => {
          if (unsubscribe && typeof unsubscribe === 'function') {
            unsubscribe();
          }
        });
      };
      
    } catch (error) {
      console.error('❌ Error setting up metrics subscription:', error);
      return null;
    }
  },

  // Generate sample activities for demo purposes
  generateSampleActivities(userId) {
    const sampleActivities = [
      {
        id: 'sample_1',
        type: 'booking',
        title: 'Booking confirmed',
        description: 'Consultation with Adv. John Doe confirmed for tomorrow at 2:00 PM',
        icon: 'booking',
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
        priority: 'high',
        data: { id: 'booking_1', status: 'confirmed' }
      },
      {
        id: 'sample_2',
        type: 'chat',
        title: 'New message received',
        description: 'Adv. John Doe: "I have reviewed your documents and prepared the case summary..."',
        icon: 'message',
        timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000), // 4 hours ago
        priority: 'medium',
        data: { id: 'chat_1' }
      },
      {
        id: 'sample_3',
        type: 'booking',
        title: 'Booking created',
        description: 'New consultation request sent to Adv. Sarah Wilson for property dispute',
        icon: 'booking',
        timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000), // 6 hours ago
        priority: 'medium',
        data: { id: 'booking_2', status: 'pending' }
      },
      {
        id: 'sample_4',
        type: 'ai',
        title: 'AI chat session',
        description: 'Asked about trademark registration process and requirements',
        icon: 'ai',
        timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000), // 8 hours ago
        priority: 'low'
      },
      {
        id: 'sample_5',
        type: 'chat',
        title: 'Chat session started',
        description: 'Started conversation with Adv. John Doe about legal consultation',
        icon: 'chat',
        timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000), // 12 hours ago
        priority: 'medium',
        data: { id: 'chat_2' }
      },
      {
        id: 'sample_6',
        type: 'document',
        title: 'Document uploaded',
        description: 'Contract agreement.pdf uploaded successfully for review',
        icon: 'document',
        timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000), // 1 day ago
        priority: 'low'
      },
      {
        id: 'sample_7',
        type: 'booking',
        title: 'Consultation completed',
        description: 'Completed consultation with Adv. Sarah Wilson - Property Law',
        icon: 'booking',
        timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000), // 2 days ago
        priority: 'medium',
        data: { id: 'booking_3', status: 'completed' }
      },
      {
        id: 'sample_8',
        type: 'ai',
        title: 'AI chat session',
        description: 'Discussed employment law and contract termination rights',
        icon: 'ai',
        timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000), // 3 days ago
        priority: 'low'
      }
    ];
    
    // Store sample activities
    localStorage.setItem('recentActivity', JSON.stringify(sampleActivities));
    
    return sampleActivities;
  },

  // Generate sample metrics for demo purposes
  generateSampleMetrics() {
    return {
      totalBookings: 8,
      pendingBookings: 2,
      completedBookings: 5,
      cancelledBookings: 1,
      totalChats: 12,
      activeChats: 3,
      unreadMessages: 4,
      totalSpent: 2500.00,
      lastActivity: new Date(Date.now() - 2 * 60 * 60 * 1000),
      averageConsultationTime: 45,
      satisfactionRate: 4.8,
      responseTime: 2.5
    };
  }
};

export default dashboardService;
